import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  modifyImage as modifyImageService,
  InsufficientCreditsError,
} from "@/services/imageGenerationService";
import { trackModificationAttempt } from "@/utils/analytics";
import { logger } from "@/utils/logger";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { createDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import type { DesignSpec } from "@/types/designSpec";
import type { DesignRenderExecutionOptions } from "@/lib/designer/applyDesignChanges";
import { useCreatePageStore } from "@/stores/createPageStore";
import { prepareSavePointPayload } from "../utils/formStateHelpers";
import {
  GENERATION_WATCHDOG_TIMEOUT_MS,
  type OnImageComplete,
} from "./useGenerationActions";
import { ActionHookContext } from "./types";

function modificationFailureMessage(view: string): string {
  return `Failed to modify ${view} image, please try again.`;
}

export function useModificationActions(
  context: ActionHookContext,
  onModifyComplete?: OnImageComplete | null
) {
  const {
    store,
    stateRef,
    session,
    isAnonymous,
    roles,
    rolesLoading,
    isChatMode,
    identityEpochRef,
  } = context;

  // Get credit-related values from context
  const { refresh: invalidateCredits, hasSufficientCredits } =
    useCreditsContext();

  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modifyRunRef = useRef(0);
  const projectActionEpoch = useCreatePageStore(
    current => current.projectActionEpoch
  );

  const clearModifyWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    modifyRunRef.current += 1;
    clearModifyWatchdog();
  }, [clearModifyWatchdog, projectActionEpoch]);

  useEffect(() => clearModifyWatchdog, [clearModifyWatchdog]);

  const modifyImage = useCallback(
    async (
      promptOverride?: string,
      designSpecOverride?: DesignSpec,
      options?: DesignRenderExecutionOptions
    ) => {
      if (!session) return;
      const identityEpoch = identityEpochRef.current;
      const owningProjectActionEpoch =
        useCreatePageStore.getState().projectActionEpoch;

      // Read at invocation time so a manual details edit and an immediate
      // "Update preview" click freeze the just-edited draft, not the previous
      // React render captured by stateRef.
      const currentState = useCreatePageStore.getState();
      if (
        currentState.ui.modification.isModifying ||
        currentState.generation.progress.loading
      )
        return;

      const promptSource =
        promptOverride ?? currentState.ui.modification.prompt;
      if (
        promptOverride !== undefined &&
        promptOverride !== currentState.ui.modification.prompt
      ) {
        store.setModifyPrompt(promptOverride);
      }
      const trimmedPrompt = promptSource.trim();
      if (!trimmedPrompt) {
        store.setError("Describe what you’d like to change.");
        return;
      }

      // No minimum length here: "shorter" is a complete edit request, and
      // modifyImage anchors anything under the endpoint's floor to the existing
      // design rather than bouncing it back at the customer.
      const modifyingFront =
        (options?.side ?? currentState.ui.view) === "front";
      if (
        !modifyingFront &&
        projectSavedBackStaleness(currentState.generation.images).stale
      ) {
        store.setError(
          "Back is out of date. Update it from the current front before editing this image."
        );
        return;
      }
      const baseImagePath = modifyingFront
        ? currentState.generation.images.front.path
        : currentState.generation.images.back.path;

      if (!baseImagePath) {
        store.setError("We couldn't save your design. Please try again.");
        return;
      }

      // Track modification attempt
      trackModificationAttempt({
        modification_type: modifyingFront ? "front" : "back",
        prompt: trimmedPrompt,
        step_index: currentState.form.currentStep,
      });

      // Wait for roles to load before checking admin status.
      //
      // Anonymous callers are exempt for the same reason as the generate path
      // (useGenerationActions): their free render is granted server-side inside
      // the transaction that spends it, so their client-visible balance is
      // legitimately 0 right up until the render happens. Pre-checking here
      // would refuse the one request that was going to succeed, and would show
      // a top-up modal to someone with no account to top up.
      const creditCheck =
        !isAnonymous && !rolesLoading && !roles.admin
          ? hasSufficientCredits(1)
          : true;
      // If credits are still loading (null), wait
      if (creditCheck === null) {
        logger.debug("Credits still loading, waiting before modification", {
          component: "CreatePageActions",
        });
        return;
      }
      // If credits are insufficient (false), show modal
      if (creditCheck === false) {
        // Save state BEFORE showing modal - preserve the original step where user ran out of credits
        // This ensures we can restore to step 4 after credit replenishment via social/feedback
        const formDataToSave = prepareSavePointPayload(currentState);

        logger.info(
          "Saving state before showing credit modal for modification",
          {
            component: "useModificationActions",
            action: "modifyImage",
            originalStep: currentState.form.currentStep,
            savedStep: formDataToSave.currentStep,
            modifyPrompt: trimmedPrompt,
            modifyingFront,
            hasImages: !!(
              currentState.generation.images.front.url ||
              currentState.generation.images.front.path
            ),
          }
        );

        FormPersistenceService.saveState({
          data: formDataToSave,
          source: "credit_modal_shown",
        });

        // Reset dismissed flag so modal can show when user explicitly tries to modify
        store.setCreditModalDismissed(false);
        store.setShowCreditModal("modify");
        return;
      }

      // The modify request has no client abort either, and a hang here is
      // worse than a hung generate: `isModifying` gates usePresetDispatch, so
      // every later button click in the chat is swallowed with no message —
      // the "Apply changes does nothing" report. Bound it on the same deadline.
      const run = modifyRunRef.current + 1;
      modifyRunRef.current = run;

      try {
        store.modifyStart(options?.side);
        clearModifyWatchdog();
        watchdogRef.current = setTimeout(() => {
          watchdogRef.current = null;
          // Only an unsettled request reaches this: the finally below clears
          // the timer, and a newer modify has moved the run token on.
          if (
            modifyRunRef.current !== run ||
            identityEpochRef.current !== identityEpoch ||
            useCreatePageStore.getState().projectActionEpoch !==
              owningProjectActionEpoch
          ) {
            return;
          }
          store.modifyError(
            modificationFailureMessage(modifyingFront ? "front" : "back")
          );
          // A retry after the watchdog owns a new run; the late transport must
          // not replace its image or complete a retired multi-view batch.
          modifyRunRef.current += 1;
          logger.error("Modification stalled past its deadline", {
            component: "CreatePageActions",
            metadata: {
              timeoutMs: GENERATION_WATCHDOG_TIMEOUT_MS,
              modifyingFront,
            },
          });
        }, GENERATION_WATCHDOG_TIMEOUT_MS);

        // Get original prompts for aggregation
        const originalPrompt = modifyingFront
          ? currentState.form.data.prompt
          : undefined;
        const originalBackPrompt = modifyingFront
          ? undefined
          : currentState.form.data.backPrompt;

        // Pass the source references through to the generation service. When a
        // reviewed DesignSpec is present, that service uses only its ordered,
        // side-scoped URLs so removed/stale form uploads cannot return.
        const formData = currentState.form.data as {
          uploadedFrontImages?: File[];
          uploadedBackImages?: File[];
          uploadedFrontImageUrls?: string[];
          uploadedBackImageUrls?: string[];
        };
        const uploadedFiles = modifyingFront
          ? formData.uploadedFrontImages
          : formData.uploadedBackImages;
        const uploadedUrls = modifyingFront
          ? formData.uploadedFrontImageUrls
          : formData.uploadedBackImageUrls;
        const draftDesignSpec = createDesignSpecSnapshot(
          designSpecOverride ??
            compileCurrentDesignSpec(
              currentState.agent.designBrief,
              currentState.form.data,
              {
                hasFrontImage: Boolean(
                  currentState.generation.images.front.url
                ),
                hasBackImage: Boolean(currentState.generation.images.back.url),
              }
            )
        );
        const sourceFrontPath = currentState.generation.images.front.path;
        const designSpec =
          !modifyingFront && draftDesignSpec && sourceFrontPath
            ? createDesignSpecSnapshot({
                ...draftDesignSpec,
                renderSource: { frontImagePath: sourceFrontPath },
              })
            : draftDesignSpec;

        const result = await modifyImageService({
          modificationPrompt: trimmedPrompt,
          baseImagePath,
          isModifyingFront: modifyingFront,
          agentSessionId: currentState.agent.sessionId,
          designSpec: designSpec ?? undefined,
          originalPrompt,
          originalBackPrompt,
          preset_type: currentState.form.data.preset_type,
          color: currentState.form.data.color,
          colorHex: currentState.form.data.colorHex,
          length: currentState.form.data.length,
          sleeve: currentState.form.data.sleeve,
          sleeveAddons: currentState.form.data.sleeveAddons,
          neckline: currentState.form.data.neckline,
          waistline: currentState.form.data.waistline,
          silhouette: currentState.form.data.silhouette,
          fabric: currentState.form.data.fabric,
          ...(uploadedFiles?.length && { uploadedImages: uploadedFiles }),
          ...(uploadedUrls?.length && { uploadedImageUrls: uploadedUrls }),
        });
        const renderedDesignSpec = createDesignSpecSnapshot(
          result.designSpec && !modifyingFront
            ? { ...result.designSpec, renderSource: designSpec?.renderSource }
            : (result.designSpec ?? designSpec)
        );

        if (
          modifyRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          useCreatePageStore.getState().projectActionEpoch !==
            owningProjectActionEpoch
        ) {
          return;
        }

        // Convert variations to state format
        const variations = result.variations.map(v => ({
          imageUrl: v.imageUrl,
          imagePath: v.imagePath,
        }));

        // Store ticket IDs from API response - tickets are created server-side.
        // Each modification creates NEW tickets server-side; the main ticketId
        // must be repointed to the newest variation[0] so subsequent actions
        // (especially submit-for-quote) target the latest generated dress,
        // not the pre-modification one.
        if (variations.length > 0) {
          for (let i = 0; i < result.variations.length; i++) {
            const ticketId = result.variations[i]?.ticketId;
            if (ticketId) {
              store.setVariationTicketId(i, ticketId);
              logger.info(
                `Ticket ID stored for ${modifyingFront ? "front" : "back"} modification variation ${i}`,
                {
                  component: "CreatePageActions",
                  metadata: {
                    ticketId,
                    variationIndex: i,
                    totalVariations: variations.length,
                    isModifyingFront: modifyingFront,
                  },
                }
              );
            } else {
              logger.warn(
                `No ticket ID returned for ${modifyingFront ? "front" : "back"} modification variation ${i}`,
                {
                  component: "CreatePageActions",
                  metadata: {
                    variationIndex: i,
                    totalVariations: variations.length,
                  },
                }
              );
            }
          }

          // Read the new ticketId directly from the API response — reading from
          // stateRef.current here is stale (React useEffect has not yet synced
          // the ref after the Zustand updates above), which previously caused
          // submit-for-quote to target the pre-modification ticket.
          const firstVariationTicketId = result.variations[0]?.ticketId;
          if (firstVariationTicketId) {
            store.setTicketId(firstVariationTicketId);
            logger.info("Set main ticketId from first modification variation", {
              component: "CreatePageActions",
              metadata: {
                ticketId: firstVariationTicketId,
                variationIndex: 0,
                isModifyingFront: modifyingFront,
              },
            });
          }
        }

        // Update state with variations (this sets isModifying = false)
        if (modifyingFront) {
          store.modifyFrontSuccess({
            variations,
            designSpec: renderedDesignSpec,
          });
        } else {
          store.modifyBackSuccess({
            variations,
            designSpec: renderedDesignSpec,
          });
        }

        // Show the modified render in the transcript immediately, decoupled from
        // the follow-up review turn (a second agent call that can fail and
        // otherwise strand the image). The agent adds its text-only review next.
        if (isChatMode && variations.length > 0) {
          const imageUrl = variations[0]?.imageUrl;
          if (imageUrl) {
            const variationUrls = variations.map(v => v.imageUrl);
            const side = modifyingFront ? "front" : "back";
            store.addMessage({
              role: "assistant",
              content: "",
              imageUrl,
              imageUrls: variationUrls,
              imageSide: side,
              selectedIndex: 0,
            });
            if (onModifyComplete && !options?.suppressAgentReview) {
              onModifyComplete(
                side,
                imageUrl,
                variationUrls,
                true,
                result.imageReviewToken
              );
            }
          }
        }

        // Refresh credits after successful generation (credit was deducted)
        invalidateCredits().catch(error => {
          logger.warn("Failed to refresh credits after modification", {
            component: "CreatePageActions",
            error: error instanceof Error ? error.message : String(error),
          });
        });
      } catch (error) {
        if (
          modifyRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          useCreatePageStore.getState().projectActionEpoch !==
            owningProjectActionEpoch
        ) {
          return;
        }
        // Error handling: ensure isModifying is set to false on any error
        if (error instanceof InsufficientCreditsError) {
          store.setIsModifying(false);
          // Same anonymous split as the generate paths: a visitor who has spent
          // their free render needs the sign-up-and-buy wall, not a top-up
          // modal for an account they do not have.
          store.setShowCreditModal(
            error.wall === "anonymous_free_render_spent"
              ? "anon_unlock"
              : "modify"
          );
        } else {
          const currentState = stateRef.current;
          const errorMessage =
            error instanceof Error
              ? error.message
              : modificationFailureMessage(currentState.ui.view);
          store.modifyError(errorMessage); // This also sets isModifying = false
        }
      } finally {
        // A newer modify owns the timer once it has moved the run token on.
        if (
          modifyRunRef.current === run &&
          identityEpochRef.current === identityEpoch &&
          useCreatePageStore.getState().projectActionEpoch ===
            owningProjectActionEpoch
        ) {
          clearModifyWatchdog();
        }
      }
    },
    [
      store,
      session,
      // Gates the client-side credit pre-check; stale here means an anonymous
      // visitor is refused a modify the server would have served.
      isAnonymous,
      roles,
      rolesLoading,
      hasSufficientCredits,
      stateRef,
      invalidateCredits,
      isChatMode,
      onModifyComplete,
      clearModifyWatchdog,
      identityEpochRef,
    ]
  );

  const setShowModifyInput = useCallback(
    (show: boolean) => {
      store.setModifyInput(show);
    },
    [store]
  );

  const setModifyPrompt = useCallback(
    (prompt: string) => {
      store.setModifyPrompt(prompt);
    },
    [store]
  );

  return {
    modifyImage,
    setShowModifyInput,
    setModifyPrompt,
  };
}
